import {CloseContent, WidgetConfig} from "../types/config";
import {ErrorTypes, FeedbackReason, Salutation, SystemMessage} from "../types/enums";

export abstract class DefaultConfig implements WidgetConfig{

    abstract id: string;
    abstract name: string;
    abstract trademark: string;
    botChatDomain: string = 'chat.drillisch-online.de';
    agentChatDomain: string = 'agentchat.drillisch-online.de';
    botChatWebsocketEndpoint: string = '/ws2';
    agentChatWebsocketEndpoint: string = '/ws-agent';
    salutation = Salutation.SIE;
    headerBotText = '{shop} KI-Assistent';
    headerAgentText = '{shop} Assistent';
    trademarkWebsite = '';
    trademarkName = '';
    agentsAvailabilityCheckInterval = 5;
    maxAgentsAvailabilityCheckCalls = 1200;
    maxAiHistoryTextLength = 2000;
    allowedExternalBaseDomains: Set<string> =  new Set<string>([
        'drillisch-online.de',
        'winsim.de',
        'premiumsim.de',
        'bigsim.de',
        'bildconnect.de',
        'blacksim.de',
        'cybersim.de',
        'gmx.net',
        'web.de',
        'handyvertrag.de',
        'maxxim.de',
        'sim24.de',
        'sim.de',
        'simdiscount.de',
        'simplytel.de',
        'smartmobil.de',
        'unlimited24.de',
        'yourfone.de',
    ]);
    allowedExternalSubdomains: Set<string> = new Set<string>([
        'imagepool',
        'www',
        'service'
    ]);

    chatSuggestions: Set<string> = new Set<string>([
        'NEU: Was hat sich an der Startseite verändert?',
        'Wie nutze ich den KI-Assistenten?',
    ]);

    reasonCodeLabels: ReadonlyMap<FeedbackReason, string> = new Map<FeedbackReason, string>([
        [FeedbackReason.NOT_UNDERSTOOD, 'Mein Anliegen wurde nicht richtig verstanden.'],
        [FeedbackReason.UNCLEAR, 'Die Antwort war unklar oder schwer verständlich.'],
        [FeedbackReason.NOT_HELPFUL, 'Die Antwort war nicht hilfreich.'],
        [FeedbackReason.OTHER, 'Sonstiges.']
    ]);

    protected feedbackConfirmationText: string = 'Vielen Dank für das Feedback.';
    protected maxFeedbackFreeTextLength: number = 350;
    protected maxChatTextLength: number = 2000;

    getWssUrlForBot(): string {
        return 'wss://' + this.botChatDomain + this.botChatWebsocketEndpoint;
    }

    getWssUrlForAgent(): string {
        return 'wss://' + this.agentChatDomain + this.agentChatWebsocketEndpoint;

    }

    getAgentChatAvailabilityUrl(): string {
        return 'https://' + this.agentChatDomain + '/availability/check?marke=' + this.trademark + '&chatart=';
    }

    getSpeechToTextUrl(): string {
        return 'https://' + this.botChatDomain + '/speech-to-text';
    }

    getTrademarkName(): string {
        return this.name;
    }

    getTrademarkWebsite(): string {
        return this.trademarkWebsite;
    }

    getHeaderText(isAgentChat: boolean = false): string {
        const headerText = isAgentChat ? this.headerAgentText : this.headerBotText;
        return headerText.replace('{shop}', this.name)
    }

    getUsernameSubHeaderText(): string {
        if (this.salutation === Salutation.SIE) {
            return 'Damit wir Sie persönlich ansprechen können, geben Sie bitte Ihren Namen an.';
        }

        return 'Damit wir Dich persönlich ansprechen können, gib bitte Deinen Namen an.';
    }

    getFirstnamePlaceholder(): string {
        if (this.salutation === Salutation.SIE) {
            return 'Geben Sie hier Ihren Vornamen ein.';
        }

        return 'Gib hier Deinen Vornamen ein.';
    }

    getLastnamePlaceholder(): string {
        if (this.salutation === Salutation.SIE) {
            return 'Geben Sie hier Ihren Nachnamen ein.';
        }

        return 'Gib hier Deinen Nachnamen ein.';
    }

    getLastnameError(): string {
        if (this.salutation === Salutation.SIE) {
            return 'Geben Sie bitte Ihren Nachnamen ein.';
        }

        return 'Gib bitte Deinen Nachnamen ein.';
    }

    getAgreementError(): string {
        if (this.salutation === Salutation.SIE) {
            return 'Akzeptieren Sie bitte die Nutzungsbedingungen.';
        }

        return 'Akzeptiere bitte die Nutzungsbedingungen.';
    }

    getQuestionPlaceholder(): string {
        if (this.salutation === Salutation.SIE) {
            return 'Geben Sie eine Nachricht ein ...';
        }

        return 'Gib eine Nachricht ein ...';
    }

    getCloseContent(): CloseContent {
        return {
            line1: this.getCloseContent1(),
            line2: this.getCloseContent2(),
            line3: this.getCloseContent3(),
        };
    }

    getNoAgentAvailableCloseContent(): CloseContent {
        if (this.salutation === Salutation.SIE) {
            return {
                line1: 'Momentan ist leider kein Berater verfügbar.',
                line2: 'Bitte versuchen Sie es später noch einmal.',
                line3: '',
            };
        }
        return {
            line1: 'Momentan ist leider kein Berater verfügbar.',
            line2: 'Bitte versuche es später noch einmal.',
            line3: '',
        };
    }

    getCloseContent1(): string {
        return 'Vielen Dank, dass ' +
            (this.salutation === Salutation.SIE ? 'Sie unseren Chat genutzt haben' : 'Du unseren Chat genutzt hast') +
            '.';
    }

    getCloseContent2(): string {
        return 'Falls ' +
            (this.salutation === Salutation.SIE ? 'Sie noch Unterstützung benötigen, helfen wir Ihnen gerne weiter.' : 'Du noch Unterstützung benötigst, helfen wir Dir gerne weiter.');
    }

    getCloseContent3(): string {
        return 'Wir sind jederzeit gerne für ' +
            (this.salutation === Salutation.SIE ? 'Sie' : 'Dich') +
            ' da.';
    }

    getAgentsAvailabilityCheckInterval(): number {
        return this.agentsAvailabilityCheckInterval;
    }

    getMaxAgentsAvailabilityCheckCalls(): number {
        return this.maxAgentsAvailabilityCheckCalls;
    }

    getSystemMessage(type: string): string {
        switch (type) {
            case SystemMessage.CONNECT_TO_AGENT:
                return (this.salutation === Salutation.SIE ? 'Sie werden' : 'Du wirst') +
                    ' mit einem Berater verbunden.<br>Nur noch einen Augenblick.';
            case SystemMessage.LOAD_CHAT:
                return 'Der Assistent wird geladen.<br>Nur noch einen Augenblick.';
            case SystemMessage.LOAD_HISTORY:
                return (this.salutation === Salutation.SIE ? 'Ihre' : 'Deine') +
                    ' Unterhaltung wird geladen.<br>Nur noch einen Augenblick.';
            case SystemMessage.LOAD_BOT_ANSWER:
                return 'Die Antwort wird gerade aufbereitet.<br>Wir bitten um ' + (this.salutation === Salutation.SIE ? 'Ihre' : 'Deine') + ' Geduld...';
            default:
                return '';
        }
    }

    getErrorMessage(type: string): string {
        switch (type) {
            case ErrorTypes.INVALID_CHAT_SESSION:
                return (this.salutation === Salutation.SIE ? 'Ihre' : 'Deine')
                + ' Sitzung ist abgelaufen. Bitte ' + (this.salutation === Salutation.SIE ? 'loggen Sie sich' : 'logge dich') + ' erneut ein.';
            case ErrorTypes.INIT_CONNECTION_ERROR:
                return 'Im Moment ist keine Verbindung möglich. Bitte ' + (this.salutation === Salutation.SIE ? 'versuchen Sie' : 'versuche') + ' es später erneut.';
            default:
                return 'Es ist ein Fehler aufgetreten. Bitte ' + (this.salutation === Salutation.SIE ? 'versuchen Sie' : 'versuche') + ' es später erneut.';
        }
    }

    getAllowedLinkBaseDomains(): Set<string> {
        return this.allowedExternalBaseDomains;
    }

    getAllowedLinkSubdomains(): Set<string> {
        return this.allowedExternalSubdomains;
    }

    getMaxAiHistoryTextLength(): number {
        return this.maxAiHistoryTextLength;
    }

    getMaxChatTextLength(): number {
        return this.maxChatTextLength;
    }

    getBotGreeting(): string {
        const greeting = `Ich unterstütze ${this.salutation === Salutation.SIE ? 'Sie zu all Ihren' : 'Dich zu all Deinen'} Fragen rund um {shop} Produkte und Services. Wie kann ich ${this.salutation === Salutation.SIE ? 'Ihnen' : 'Dir'} heute helfen?`;
        return greeting.replace('{shop}', this.name);
    }

    getSuggestions(): Set<string> {
        return this.chatSuggestions;
    }

    getFeedbackConfirmationText(): string {
        return this.feedbackConfirmationText
    }

    getMaxFeedbackFreeTextLength(): number {
        return this.maxFeedbackFreeTextLength;
    }

    getLabelForReasonCode(reasonCode: FeedbackReason): string {
        if (this.reasonCodeLabels.has(reasonCode)) {
            return this.reasonCodeLabels.get(reasonCode)!;
        }

        return '';
    }

    get feedbackSubmitConfirmationText(): string {
        return `${this.salutation === Salutation.SIE ? 'Ihr' : 'Dein'} Feedback wurde gespeichert`;
    }
}
